/*
 * Lilla Flocken – Skymningspromenaden.
 * Small jobs only: section parts arrive once in reading order, the sunset clock shows when the sun sets
 * today in a Swedish town (and rolls its digits there), the day/night switch for the harness, links that
 * open a section of the product description, films that play only while seen (with a pause button), and
 * the held belt story, the header's scroll edge and the touch listener iOS needs for press feedback.
 * Round 10 ("Ritualen"): today's sunset line in "Dag och natt" and the small AI labels in product galleries.
 * Everything is readable without this file; motion stays off under prefers-reduced-motion.
 */
(() => {
  const root = document.documentElement;
  root.classList.add('lf-js', 'lf-ready');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasObserver = 'IntersectionObserver' in window;

  /* Observe against this page's own viewport, also when the page sits in a frame (the theme editor):
     with the implicit root, margins in percent would be measured on the outer window. Browsers that
     do not accept a document as root fall back to the implicit one. */
  function watch(callback, options) {
    try {
      return new IntersectionObserver(callback, { ...options, root: document });
    } catch (error) {
      return new IntersectionObserver(callback, options);
    }
  }

  /* Section parts arrive once, heading first. */
  function initReveals(scope) {
    const sections = [...scope.querySelectorAll('[data-lf-reveal]:not([data-lf-reveal-set])')];
    sections.forEach((section) => {
      section.setAttribute('data-lf-reveal-set', '');
      [...section.children].forEach((child, index) => child.style.setProperty('--lf-i', String(Math.min(index, 4))));
    });
    if (!hasObserver || reduceMotion.matches) {
      sections.forEach((section) => section.classList.add('is-in'));
      return;
    }
    /* A wrapper with display: contents (the day/night text on a phone) has no box of its own to observe,
       so its first child stands in for it. */
    const owners = new Map();
    const targetOf = (section) => {
      if (window.getComputedStyle(section).display !== 'contents') return section;
      const stand = section.firstElementChild || section;
      owners.set(stand, section);
      return stand;
    };
    const observer = watch(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          (owners.get(entry.target) || entry.target).classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );
    sections.forEach((section) => observer.observe(targetOf(section)));
  }

  /* Films: fetched when near the screen, muted loops while seen, a pause button on every one (WCAG 2.2.2).
     A film the visitor paused stays paused. Under reduced motion nothing starts by itself; the button
     shows "play" and starts the film on request. */
  function initVideos(scope) {
    const videos = [...scope.querySelectorAll('video[data-lf-video]:not([data-lf-video-set])')];
    if (!videos.length) return;

    const setButton = (video) => {
      const button = video._lfButton;
      if (!button) return;
      const playing = !video.paused;
      button.classList.toggle('is-paused', !playing);
      button.setAttribute('aria-label', playing ? 'Pausa film' : 'Spela film');
    };

    const start = (video) => {
      if (video.preload === 'none') video.preload = 'auto';
      video.muted = true;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => setButton(video));
    };

    videos.forEach((video) => {
      video.setAttribute('data-lf-video-set', '');
      const frame = video.parentElement;
      const button = frame ? frame.querySelector('[data-lf-pause]') : null;
      video._lfButton = button;
      video.addEventListener('playing', () => {
        video.classList.add('is-playing');
        setButton(video);
      });
      video.addEventListener('pause', () => setButton(video));
      if (button) {
        button.hidden = false;
        button.addEventListener('click', () => {
          if (video.paused) {
            video.dataset.lfUserPaused = '';
            delete video.dataset.lfUserPaused;
            start(video);
          } else {
            video.dataset.lfUserPaused = 'true';
            video.pause();
          }
        });
      }
      setButton(video);
    });

    const autoplayAllowed = (video) => !reduceMotion.matches && video.dataset.lfUserPaused !== 'true';

    if (!hasObserver) {
      videos.forEach((video) => {
        if (autoplayAllowed(video)) start(video);
      });
      return;
    }

    /* Warm the file a little before the film is seen, play it only while at least 15 % is visible. */
    const warm = watch(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target.preload === 'none') entry.target.preload = 'metadata';
          warm.unobserve(entry.target);
        }
      },
      { rootMargin: '400px 0px' }
    );
    const view = watch(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (autoplayAllowed(video)) start(video);
          } else if (!video.paused) {
            video.pause();
          }
        }
      },
      { threshold: 0.15 }
    );
    videos.forEach((video) => {
      warm.observe(video);
      view.observe(video);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach((video) => video.pause());
    });
  }

  /* Held story: the step crossing the middle of the screen is the lit one (large screens only). */
  function initSteps(scope) {
    scope.querySelectorAll('[data-lf-story]:not([data-lf-story-set])').forEach((section) => {
      section.setAttribute('data-lf-story-set', '');
      const steps = [...section.querySelectorAll('[data-lf-step]')];
      if (!steps.length || !hasObserver) return;
      section.classList.add('is-story-live');
      const light = (active) => steps.forEach((step) => step.classList.toggle('is-active', step === active));
      light(steps[0]);
      const observer = watch(
        (entries) => {
          for (const entry of entries) if (entry.isIntersecting) light(entry.target);
        },
        { rootMargin: '-45% 0px -45% 0px' }
      );
      steps.forEach((step) => observer.observe(step));
    });
  }

  /* The sunset clock: NOAA solar equations, Swedish local time, no external service. */
  const TIME_ZONE = 'Europe/Stockholm';
  const CITIES = {
    malmo: [55.605, 13.003],
    goteborg: [57.709, 11.974],
    stockholm: [59.329, 18.069],
    uppsala: [59.858, 17.639],
    ostersund: [63.179, 14.636],
    umea: [63.826, 20.263],
    lulea: [65.584, 22.155],
    kiruna: [67.856, 20.225],
  };

  function todayInSweden() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const part = (type) => Number(parts.find((p) => p.type === type).value);
    return [part('year'), part('month') - 1, part('day')];
  }

  function sunset([year, month, day], lat, lng) {
    const rad = Math.PI / 180;
    const dayOfYear = Math.round((Date.UTC(year, month, day) - Date.UTC(year, 0, 0)) / 86400000);
    const g = ((2 * Math.PI) / 365) * (dayOfYear - 1);
    const eqTime =
      229.18 *
      (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
    const decl =
      0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) +
      0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
    const cosH = Math.cos(90.833 * rad) / (Math.cos(lat * rad) * Math.cos(decl)) - Math.tan(lat * rad) * Math.tan(decl);
    if (cosH > 1) return { polar: 'night' };
    if (cosH < -1) return { polar: 'day' };
    const hourAngle = Math.acos(cosH) / rad;
    return { set: new Date(Date.UTC(year, month, day) + (720 - 4 * (lng - hourAngle) - eqTime) * 60000) };
  }

  function clockText(date) {
    const parts = new Intl.DateTimeFormat('sv-SE', {
      timeZone: TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const part = (type) => parts.find((p) => p.type === type).value;
    return `${part('hour')}.${part('minute')}`;
  }

  function buildRoll(element) {
    element.textContent = '';
    const strips = [];
    for (let column = 0; column < 4; column += 1) {
      if (column === 2) {
        const dot = document.createElement('span');
        dot.className = 'lf-roll__dot';
        dot.textContent = '.';
        element.append(dot);
      }
      const col = document.createElement('span');
      col.className = 'lf-roll__col';
      const strip = document.createElement('span');
      strip.className = 'lf-roll__strip';
      strip.style.setProperty('--lf-c', String(column));
      for (let digit = 0; digit <= 9; digit += 1) {
        const cell = document.createElement('span');
        cell.textContent = String(digit);
        strip.append(cell);
      }
      col.append(strip);
      element.append(col);
      strips.push(strip);
    }
    return strips;
  }

  function setRoll(strips, text) {
    const digits = text.replace('.', '').split('').map(Number);
    strips.forEach((strip, index) => strip.style.setProperty('--lf-d', String(digits[index] || 0)));
  }

  function initSun(scope) {
    scope.querySelectorAll('[data-lf-sun]:not([data-lf-sun-set])').forEach((section) => {
      section.setAttribute('data-lf-sun-set', '');
      const clock = section.querySelector('[data-lf-sun-clock]');
      const roll = section.querySelector('[data-lf-roll]');
      const answer = section.querySelector('[data-lf-sun-answer]');
      const sentence = section.querySelector('[data-lf-sun-sentence]');
      const select = section.querySelector('[data-lf-city]');
      if (!clock || !roll || !answer || !sentence || !select) return;

      let city = CITIES[section.dataset.defaultCity] ? section.dataset.defaultCity : 'stockholm';
      try {
        const saved = window.localStorage.getItem('lf-ort');
        if (saved && CITIES[saved]) city = saved;
      } catch (error) {
        /* Storage can be blocked; the default town is fine. */
      }
      select.value = city;

      const strips = buildRoll(roll);
      clock.hidden = false;
      answer.hidden = false;
      let started = false;

      const render = () => {
        const [lat, lng] = CITIES[select.value] || CITIES.stockholm;
        const times = sunset(todayInSweden(), lat, lng);
        if (times.polar) {
          clock.classList.add('is-polar');
          sentence.textContent =
            times.polar === 'night' ? 'Solen går inte upp alls i dag i' : 'Solen går inte ner alls i dag i';
          return;
        }
        clock.classList.remove('is-polar');
        const text = clockText(times.set);
        const afterMidnight = Number(text.slice(0, 2)) < 12;
        sentence.textContent = afterMidnight ? 'Solen går ner först efter midnatt i' : `Solen går ner kl. ${text} i`;
        clock.dataset.time = text;
        if (started) setRoll(strips, text);
      };

      const start = () => {
        started = true;
        if (clock.dataset.time) setRoll(strips, clock.dataset.time);
      };

      render();
      if (hasObserver && !reduceMotion.matches) {
        const observer = watch(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            start();
            observer.disconnect();
          },
          { threshold: 0.35 }
        );
        observer.observe(clock);
      } else {
        start();
      }

      select.addEventListener('change', () => {
        try {
          window.localStorage.setItem('lf-ort', select.value);
        } catch (error) {
          /* Not remembered this time; the clock still updates. */
        }
        render();
      });
    });
  }

  /* Round 10: one live line in "Dag och natt" with today's sunset in Stockholm (59.33 N, 18.07 E), worked
     out here with the same NOAA equations as the clock, in Swedish time. Before sunset the line says "går",
     after it "gick". If anything cannot be worked out the line stays hidden, and it never moves the page
     above it: it sits last in its text column, several screens below the first one. */
  const STOCKHOLM = [59.33, 18.07];

  function initSunline(scope) {
    scope.querySelectorAll('[data-lf-sunline]:not([data-lf-sunline-set])').forEach((line) => {
      line.setAttribute('data-lf-sunline-set', '');
      try {
        const times = sunset(todayInSweden(), STOCKHOLM[0], STOCKHOLM[1]);
        if (!times.set || Number.isNaN(times.set.getTime())) return;
        const text = clockText(times.set);
        if (!/^\d{2}\.\d{2}$/.test(text)) return;
        const past = Date.now() >= times.set.getTime();
        const template = (past ? line.dataset.textPast : line.dataset.text) || line.dataset.text || '';
        if (!template.includes('[tid]')) return;
        line.textContent = template.replace('[tid]', text);
        line.hidden = false;
      } catch (error) {
        line.hidden = true;
      }
    });
  }

  /* Round 10: the small "AI-bild" / "AI-film" label in product galleries and product cards. The footer
     lists the AI-generated product media by id (script[data-lf-ai-media]); every element that carries
     one of those ids gets the label once (the outermost element with the id, never a nested copy). */
  function initAiLabels(scope) {
    const source = document.querySelector('script[data-lf-ai-media]');
    if (!source) return;
    let ids = [];
    try {
      ids = JSON.parse(source.textContent).map(String).filter(Boolean);
    } catch (error) {
      return;
    }
    if (!ids.length) return;
    scope.querySelectorAll('[data-media-id]:not([data-lf-ai-set])').forEach((element) => {
      element.setAttribute('data-lf-ai-set', '');
      const id = String(element.getAttribute('data-media-id'));
      if (!ids.includes(id)) return;
      const outer = element.parentElement ? element.parentElement.closest(`[data-media-id="${id}"]`) : null;
      if (outer) return;
      const film = element.querySelector('video, deferred-media, iframe, [data-video]') !== null;
      const label = document.createElement('span');
      label.className = 'lf-ai';
      label.textContent = film ? 'AI-film' : 'AI-bild';
      element.setAttribute('data-lf-ai-host', '');
      element.append(label);
    });
  }

  /* Day and night: one stage, two pictures, two buttons. */
  function initDayNight(scope) {
    scope.querySelectorAll('[data-lf-daynight]:not([data-lf-daynight-set])').forEach((section) => {
      section.setAttribute('data-lf-daynight-set', '');
      const group = section.querySelector('[data-lf-switch]');
      const buttons = [...section.querySelectorAll('[data-lf-show]')];
      if (!group || buttons.length < 2) return;
      group.hidden = false;
      section.classList.add('is-switchable');
      const show = (state) => {
        section.dataset.state = state;
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.lfShow === state)));
      };
      buttons.forEach((button) => button.addEventListener('click', () => show(button.dataset.lfShow)));
      show(section.dataset.state || 'dag');
    });
  }

  /* Links that open one section of the product description (the size guide). */
  /* Horizon scrolls .page-wrapper on desktop and the document on phones; the product details column
     is sticky, so one jump may not be enough: step until the section sits in the upper half. */
  function scroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible') {
      return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  async function bringIntoView(element) {
    const container = scroller();
    for (let step = 0; step < 5; step += 1) {
      const top = element.getBoundingClientRect().top;
      if (top > 72 && top < window.innerHeight * 0.45) return;
      const before = container.scrollTop;
      container.scrollBy({ top: top - 120, behavior: 'instant' });
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      if (Math.abs(container.scrollTop - before) < 1) return;
    }
  }

  function openSection(id, scroll) {
    if (!id) return;
    const target = document.getElementById(id);
    if (!target || target.tagName !== 'DETAILS') return;
    target.open = true;
    if (!scroll) return;
    bringIntoView(target).then(() => {
      const summary = target.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    });
  }

  function initOpeners(scope) {
    scope.querySelectorAll('[data-lf-open]:not([data-lf-open-set])').forEach((link) => {
      link.setAttribute('data-lf-open-set', '');
      link.addEventListener('click', (event) => {
        const id = link.getAttribute('data-lf-open');
        if (!document.getElementById(id)) return;
        event.preventDefault();
        openSection(id, true);
      });
    });
  }

  /* Scroll edge: the header's soft edge only shows while the sticky header floats over the page. The
     header (Horizon) sticks at top -1px, so "stuck" is its box reaching the top of the viewport. */
  function initHeaderEdge() {
    const header = document.querySelector('.header-section');
    if (!header || header.hasAttribute('data-lf-edge-set')) return;
    header.setAttribute('data-lf-edge-set', '');
    /* Scroll events already arrive at most once per frame; one rect read per event is cheap. */
    const update = () => {
      const stuck = header.getBoundingClientRect().top <= 0 && scroller().scrollTop > 0;
      if (stuck !== header.hasAttribute('data-lf-stuck')) header.toggleAttribute('data-lf-stuck', stuck);
    };
    window.addEventListener('scroll', update, { passive: true });
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  function init(scope) {
    initReveals(scope);
    initVideos(scope);
    initSteps(scope);
    initSun(scope);
    initSunline(scope);
    initDayNight(scope);
    initOpeners(scope);
    initAiLabels(scope);
  }

  /* iOS Safari only shows :active (the press feedback) when a touchstart listener exists. A passive,
     empty one is enough and never delays scrolling. */
  document.addEventListener('touchstart', () => {}, { passive: true });

  init(document);
  initHeaderEdge();
  if (window.location.hash.length > 1) openSection(decodeURIComponent(window.location.hash.slice(1)), false);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
